import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveCanvasArtwork } from "../../services/artworkResolver";
import type { CanvasArtwork } from "../../data/canvasArtworks";
import { isCanvasSold } from "../../lib/canvasAvailability";
import { useCanvasArtwork } from "../../hooks/useTranslatedArtwork";
import { useArtworkDocumentMeta } from "../../hooks/useArtworkDocumentMeta";
import { artworkPagePath } from "../../lib/artworkPaths";
import { ArtworkDetailLayout } from "./ArtworkDetailLayout";
import { ArtworkImageGallery } from "../ArtworkImageGallery";
import { CanvasArtworkSpecs } from "../CanvasArtworkSpecs";
import { CanvasSoldLabel } from "../CanvasSoldLabel";
import { RelatedArtworksSection } from "../RelatedArtworksSection";

export function CanvasArtworkPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const work = resolveCanvasArtwork(id);

  if (!work) {
    return (
      <div className="section-container py-24 text-center">
        <h1 className="font-display text-3xl text-canvas-950">
          {t("common.artworkNotFound")}
        </h1>
        <Link to="/gallery?tab=canvas" className="btn-primary mt-6">
          {t("common.backToGallery")}
        </Link>
      </div>
    );
  }

  return <CanvasArtworkPageContent work={work} />;
}

function CanvasArtworkPageContent({ work }: { work: CanvasArtwork }) {
  const { t } = useTranslation();
  const translated = useCanvasArtwork(work);
  const sold = isCanvasSold(work);

  useArtworkDocumentMeta({
    title: `${translated.title} | Shanir Arts`,
    description: translated.description,
    imageUrl: translated.imageUrl,
    canonicalPath: artworkPagePath("canvas", work.id),
  });

  return (
    <ArtworkDetailLayout
      medium="canvas"
      artworkId={work.id}
      artworkName={translated.name}
      eyebrow={translated.collectionLabel}
      title={translated.title}
      description={translated.description}
      imageUrl={translated.imageUrl}
      imageAlt={translated.name}
      titleExtra={sold ? <CanvasSoldLabel /> : undefined}
      imageGallery={
        <ArtworkImageGallery
          images={translated.galleryImages}
          className="w-full"
        />
      }
      specs={<CanvasArtworkSpecs work={work} />}
      primaryAction={
        sold ? (
          <Link
            to="/contact#commission"
            className="btn-accent w-full text-center sm:w-auto"
          >
            {t("canvas.sold.cta")}
          </Link>
        ) : undefined
      }
      footer={
        <RelatedArtworksSection
          medium="canvas"
          currentId={work.id}
          collection={work.collection}
          collectionLabel={translated.collectionLabel}
        />
      }
    />
  );
}
